'use client';

import { ReactNode } from 'react';
import cn from 'classnames';
import {
    EntityCategoryImage,
    type ImageItem,
    Variant as ImageVariant,
} from '../EntityCategoryImage/EntityCategoryImage';
import { Text } from '@/ui-components/atoms/Text/Text';
import { LinkWrapperIfHref } from '@/ui-components/atoms/LinkWrapperIfHref/LinkWrapperIfHref';

import styles from './entityCategoryItem.module.scss';

const {
    mainWrapper,
    imageContainer,
    fixedHoverAnimationContainer,
    noHoverAnimation,
    textContainer,
    titleStyles,
    descriptionStyles,
    mediumVariantStyles,
    smallVariantStyles,
    rightAlignedImageStyles,
    deviceGroupStyles,
    deviceGroupDescriptionStyles,
    dropdownStyles,
    linkStyles,
} = styles;

export enum Variant {
    small = 'small',
    medium = 'medium',
    large = 'large',
    deviceGroup = 'deviceGroup',
}

export interface EntityCategoryItemProps {
    variant?: string;
    title: string;
    description?: ReactNode;
    items: ImageItem[];
    leftAlign?: boolean;
    href?: string;
    hideImage?: boolean;
    squareImage?: boolean;
    className?: string;
    isMultiple?: boolean;
    isImageSmall?: boolean;
    isInDropdown?: boolean;
}

export function EntityCategoryItem({
    title,
    description,
    variant,
    items,
    leftAlign = true,
    href,
    hideImage = false,
    squareImage = false,
    className,
    isMultiple = false,
    isImageSmall = false,
    isInDropdown = false,
}: EntityCategoryItemProps) {
    let imageType = null;
    if (variant === Variant.large) {
        imageType = (
            <EntityCategoryImage
                items={items}
                squareType={squareImage}
                isMultiple={isMultiple}
                isImageSmall={isImageSmall}
            />
        );
    } else if (variant === Variant.medium) {
        imageType = (
            <EntityCategoryImage
                items={items}
                variant={ImageVariant.single}
                squareType={squareImage}
                isMultiple={isMultiple}
                isImageSmall={isImageSmall}
            />
        );
    } else if (variant === Variant.small) {
        imageType = (
            <EntityCategoryImage
                items={items}
                variant={ImageVariant.stack}
                squareType={squareImage}
                isMultiple={isMultiple}
                isImageSmall={isImageSmall}
                isInDropdown={isInDropdown}
            />
        );
    } else if (variant === Variant.deviceGroup) {
        imageType = (
            <EntityCategoryImage
                items={items}
                variant={ImageVariant.collection}
                squareType={squareImage}
                isMultiple={isMultiple}
                isWayToListenCard={true}
            />
        );
    }

    const mainWrapperStyles = cn(mainWrapper, className, {
        [linkStyles]: Boolean(href),
        [mediumVariantStyles]: variant === Variant.medium,
        [smallVariantStyles]: variant === Variant.small,
        [deviceGroupStyles]: variant === Variant.deviceGroup,
        [dropdownStyles]: isInDropdown,
    });

    const imageWrapperStyles = cn(imageContainer, {
        [rightAlignedImageStyles]: !leftAlign && variant === Variant.small,
        [fixedHoverAnimationContainer]: !squareImage,
        [noHoverAnimation]: isImageSmall,
    });

    const itemDescriptionStyles = cn(descriptionStyles, {
        [deviceGroupDescriptionStyles]: variant === Variant.deviceGroup,
    });

    return (
        <LinkWrapperIfHref className={mainWrapperStyles} link={{ href }}>
            {!hideImage && <div className={imageWrapperStyles}>{imageType}</div>}
            <div className={textContainer}>
                <Text
                    tag="div"
                    variant={variant === Variant.deviceGroup ? 'h5' : variant === Variant.small ? 'h6' : 'h4'}
                    className={titleStyles}
                >
                    {title}
                </Text>

                {variant !== Variant.small && <div className={itemDescriptionStyles}>{description}</div>}
            </div>
        </LinkWrapperIfHref>
    );
}
